import React, { useCallback, useEffect, useState } from 'react'
import { Loader2Icon, PrinterIcon, QrCodeIcon, Trash2Icon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { downloadQrStickerPdf } from '../lib/downloadQrStickerPdf'
import {
  MobilePage,
  MobilePageHeader,
  MobileListRow,
  MobileRowAction,
  MobileEmptyState,
  MobileFilterBar,
  MobileSelect,
  MobileStatStrip,
  MobileListGroup,
  MobileSection,
  MobileSearchField,
  mobile,
} from '../components/mobile'

type Batch = { id: string; label: string; quantity: number; created_at: string }

export function QrStickerBatches() {
  const [batches, setBatches] = useState<Batch[]>([])
  const [loading, setLoading] = useState(true)
  const [label, setLabel] = useState('Sticker batch')
  const [qty, setQty] = useState(24)
  const [busy, setBusy] = useState(false)
  const [busyBatchId, setBusyBatchId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data, error: err } = await supabase
        .from('qr_sticker_batches')
        .select('id, label, quantity, created_at')
        .order('created_at', { ascending: false })
      if (err) throw err
      setBatches(data || [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load batches')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const downloadBatchPdf = async (batchId: string, batchLabel: string) => {
    const { data, error: err } = await supabase
      .from('qr_points')
      .select('id, code, status')
      .eq('batch_id', batchId)
      .order('created_at')
    if (err) throw err
    const codes = (data || []).map((p) => p.code).filter(Boolean)
    if (!codes.length) throw new Error('No stickers in this batch')
    const safeName = (batchLabel || 'qr-stickers')
      .replace(/[^a-zA-Z0-9-_ ]+/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .slice(0, 40)
    await downloadQrStickerPdf(codes, safeName || 'qr-stickers')
  }

  const createBatch = async () => {
    setBusy(true)
    setError(null)
    try {
      const { data, error: err } = await supabase.rpc('create_qr_sticker_batch', {
        p_label: label,
        p_quantity: qty,
      })
      if (err) throw err
      await load()
      await downloadBatchPdf(data as string, label)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed')
    } finally {
      setBusy(false)
    }
  }

  const deleteBatch = async (batch: Batch) => {
    const ok = window.confirm(
      `Delete batch "${batch.label}" and its ${batch.quantity} unassigned stickers? This cannot be undone.`
    )
    if (!ok) return
    setBusyBatchId(batch.id)
    setError(null)
    try {
      const { error: err } = await supabase.rpc('delete_qr_sticker_batch', {
        p_batch_id: batch.id,
      })
      if (err) throw err
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed')
    } finally {
      setBusyBatchId(null)
    }
  }

  return (
    <MobilePage>
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <QrCodeIcon className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">QR sticker batches</h1>
            <p className="text-sm text-gray-600">
              Generate a blank PDF label sheet, print stickers, then scan in the field to assign each
              one to a location.
            </p>
          </div>
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
            {error}
          </div>
        )}

        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
          <h2 className="font-semibold text-gray-900">Generate batch</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="text-sm sm:col-span-2">
              Label
              <input
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </label>
            <label className="text-sm">
              Quantity
              <input
                type="number"
                min={1}
                max={500}
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                value={qty}
                onChange={(e) => setQty(Number(e.target.value) || 1)}
              />
            </label>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={createBatch}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50"
          >
            {busy ? <Loader2Icon className="w-4 h-4 animate-spin" /> : <PrinterIcon className="w-4 h-4" />}
            Create &amp; download PDF
          </button>
        </div>

        <div className="native-mobile-list space-y-2">
          {loading ? (
            <div className="p-8 flex justify-center">
              <Loader2Icon className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : batches.length === 0 ? (
            <MobileEmptyState title="No batches yet" />
          ) : (
            batches.map((b) => (
              <MobileListRow
                key={b.id}
                title={b.label}
                subtitle={`${b.quantity} stickers · ${new Date(b.created_at).toLocaleString()}`}
                chevron={false}
                footer={
                  <>
                    <MobileRowAction
                      tone="primary"
                      onClick={() =>
                        downloadBatchPdf(b.id, b.label).catch((e) =>
                          setError(e instanceof Error ? e.message : 'PDF failed')
                        )
                      }
                    >
                      PDF
                    </MobileRowAction>
                    <MobileRowAction tone="danger" onClick={() => deleteBatch(b)}>
                      {busyBatchId === b.id ? (
                        <Loader2Icon className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2Icon className="w-4 h-4" />
                      )}
                      Delete
                    </MobileRowAction>
                  </>
                }
              />
            ))
          )}
        </div>

        <div className="native-desktop-table bg-white rounded-lg border border-gray-200">
          {loading ? (
            <div className="p-8 flex justify-center">
              <Loader2Icon className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {batches.map((b) => (
                <li key={b.id} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-gray-900">{b.label}</p>
                    <p className="text-xs text-gray-500">
                      {b.quantity} stickers · {new Date(b.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      disabled={busy || busyBatchId === b.id}
                      className="text-sm text-blue-600 hover:underline disabled:opacity-50"
                      onClick={() =>
                        downloadBatchPdf(b.id, b.label).catch((e) =>
                          setError(e instanceof Error ? e.message : 'PDF failed')
                        )
                      }
                    >
                      Download PDF
                    </button>
                    <button
                      type="button"
                      disabled={busy || busyBatchId === b.id}
                      className="inline-flex items-center gap-1 text-sm text-red-600 hover:underline disabled:opacity-50"
                      onClick={() => deleteBatch(b)}
                    >
                      {busyBatchId === b.id ? (
                        <Loader2Icon className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2Icon className="w-3.5 h-3.5" />
                      )}
                      Delete
                    </button>
                  </div>
                </li>
              ))}
              {batches.length === 0 && (
                <li className="px-4 py-8 text-center text-sm text-gray-500">No batches yet.</li>
              )}
            </ul>
          )}
        </div>
      </div>
    </MobilePage>
  )
}

export default QrStickerBatches
